import { useState } from 'react'
import type { CSSProperties } from 'react'
import type { ForecastState } from '../../types'
import type { ChartPoint } from '../../types'
import { useTranslation } from '../../lib/i18n'
import { Tooltip } from '../Tooltip'
import type { TooltipKey } from '../Tooltip'

interface StatsRowProps {
  state: ForecastState
  chart: ChartPoint[]
}

const ACCURACY_COLORS: Record<string, string> = {
  HIGH:   '#1a7f37',
  MEDIUM: '#92400e',
  LOW:    '#991b1b',
}

export function StatsRow({ state, chart: _ }: StatsRowProps) {
  const { t } = useTranslation()
  const [tooltip, setTooltip] = useState<TooltipKey | null>(null)

  const { weight_smoothed, tempo, accuracy } = state.current
  const isLow = accuracy === 'LOW' && state.status !== 'WARMUP'

  const tempoDirection = tempo !== null
    ? (tempo < 0 ? t('stats.tempo_loss') : t('stats.tempo_gain'))
    : null

  const tempoAbs = tempo !== null ? Math.abs(tempo).toFixed(2) : null

  const accuracyLabel = state.status === 'WARMUP'
    ? '—'
    : t(`stats.accuracy_${accuracy.toLowerCase()}`)
  const accuracyColor = state.status === 'WARMUP'
    ? 'var(--tg-theme-hint-color, #888)'
    : (ACCURACY_COLORS[accuracy] ?? 'var(--tg-theme-text-color, #000)')

  return (
    <>
      <div style={styles.root}>
        {/* Средний вес */}
        <div style={styles.col}>
          <div style={styles.label}>{t('stats.weight_label')}</div>
          <div style={{ ...styles.value, opacity: isLow ? 0.5 : 1 }}>
            {weight_smoothed.toFixed(1)} {t('unit.kg')}
          </div>
        </div>

        <div style={styles.divider} />

        {/* Темп */}
        <div style={styles.col}>
          <div style={styles.label}>{t('stats.tempo_label')}</div>
          {tempo !== null ? (
            <>
              <div style={styles.value}>{tempoAbs} {t('unit.kg_week')}</div>
              <div style={styles.sub}>{tempoDirection}</div>
            </>
          ) : (
            <div style={{ ...styles.value, opacity: 0.4 }}>—</div>
          )}
        </div>

        <div style={styles.divider} />

        {/* Точность прогноза */}
        <div style={styles.col}>
          <div style={styles.label}>{t('stats.accuracy_label')}</div>
          <div style={{ ...styles.value, color: accuracyColor }}>
            {accuracyLabel}
          </div>
          <button
            style={styles.accuracyBtn}
            onClick={() => setTooltip('accuracy_how' as TooltipKey)}
          >
            {t('stats.accuracy_btn')}
          </button>
        </div>
      </div>

      <Tooltip tooltipKey={tooltip} onClose={() => setTooltip(null)} />
    </>
  )
}

const styles: Record<string, CSSProperties> = {
  root: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '16px 0',
  },
  col: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 0,
  },
  divider: {
    width: 1,
    alignSelf: 'stretch',
    background: 'var(--tg-theme-secondary-bg-color, #e5e7eb)',
    margin: '0 12px',
    flexShrink: 0,
  },
  label: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 0.6,
    color: 'var(--tg-theme-hint-color, #888)',
    marginBottom: 2,
  },
  value: {
    fontSize: 17,
    fontWeight: 700,
    color: 'var(--tg-theme-text-color, #000)',
    lineHeight: 1.2,
  },
  sub: {
    fontSize: 12,
    color: 'var(--tg-theme-hint-color, #888)',
  },
  accuracyBtn: {
    background: 'none',
    border: 'none',
    padding: 0,
    fontSize: 12,
    color: 'var(--tg-theme-link-color, #007aff)',
    cursor: 'pointer',
    textAlign: 'left',
    marginTop: 2,
  },
}
